import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Users, Truck, Calendar, CheckSquare, AlertTriangle, FileWarning, MessageCircle, Settings, LayoutDashboard, MapPin, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import api from '../lib/api';

// Search categories with their routes and icons
const SEARCH_CATEGORIES = [
  { 
    id: 'employees', 
    name: 'Salariés', 
    icon: Users, 
    route: '/employees',
    searchFields: ['nom', 'prenom', 'email', 'poste', 'matricule'],
    color: 'text-blue-500'
  },
  { 
    id: 'vehicles', 
    name: 'Véhicules', 
    icon: Truck, 
    route: '/vehicles',
    searchFields: ['immatriculation', 'marque', 'modele'],
    color: 'text-green-500'
  },
  { 
    id: 'tasks', 
    name: 'Tâches', 
    icon: CheckSquare, 
    route: '/tasks',
    searchFields: ['titre', 'description'],
    color: 'text-purple-500'
  },
  { 
    id: 'incidents', 
    name: 'Incidents', 
    icon: AlertTriangle, 
    route: '/incidents',
    searchFields: ['titre', 'description', 'type'],
    color: 'text-amber-500'
  },
  { 
    id: 'warnings', 
    name: 'Avertissements', 
    icon: FileWarning, 
    route: '/warnings',
    searchFields: ['motif', 'description'],
    color: 'text-red-500'
  }
];

// Store search term globally for highlighting
let globalSearchTerm = '';
export const getGlobalSearchTerm = () => globalSearchTerm;
export const setGlobalSearchTerm = (term) => { globalSearchTerm = term; };

// Global Search Component
export const GlobalSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setSelectedCategory(null);
    }
  }, [isOpen]);

  // Search with debounce
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    const allResults = [];

    try {
      // Search employees
      const employeesRes = await api.get('/employees');
      const employees = employeesRes.data.filter(emp => 
        `${emp.nom} ${emp.prenom} ${emp.email} ${emp.poste} ${emp.matricule || ''}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      ).slice(0, 5).map(emp => ({
        ...emp,
        category: 'employees',
        displayName: `${emp.prenom} ${emp.nom}`,
        subtitle: emp.poste,
        route: `/employees/${emp.id}`
      }));
      allResults.push(...employees);

      // Search vehicles
      const vehiclesRes = await api.get('/vehicles');
      const vehicles = vehiclesRes.data.filter(v => 
        `${v.immatriculation} ${v.marque} ${v.modele}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      ).slice(0, 5).map(v => ({
        ...v,
        category: 'vehicles',
        displayName: v.immatriculation,
        subtitle: `${v.marque} ${v.modele}`,
        route: `/vehicles/${v.id}`
      }));
      allResults.push(...vehicles);

      // Search tasks
      const tasksRes = await api.get('/tasks');
      const tasks = tasksRes.data.filter(t => 
        `${t.titre} ${t.description || ''}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      ).slice(0, 5).map(t => ({
        ...t,
        category: 'tasks',
        displayName: t.titre,
        subtitle: t.statut,
        route: '/tasks'
      }));
      allResults.push(...tasks);

      // Search incidents
      const incidentsRes = await api.get('/incidents');
      const incidents = incidentsRes.data.filter(i => 
        `${i.titre} ${i.description || ''} ${i.type}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      ).slice(0, 5).map(i => ({
        ...i,
        category: 'incidents',
        displayName: i.titre,
        subtitle: i.type,
        route: '/incidents'
      }));
      allResults.push(...incidents);

      setResults(allResults);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result) => {
    setGlobalSearchTerm(query);
    navigate(result.route, { state: { searchHighlight: query } });
    onClose();
  };

  const handleCategoryClick = (category) => {
    setGlobalSearchTerm(query);
    navigate(category.route, { state: { searchHighlight: query } });
    onClose();
  };

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getCategoryIcon = (categoryId) => {
    const cat = SEARCH_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.icon : Search;
  };

  const getCategoryColor = (categoryId) => {
    const cat = SEARCH_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.color : 'text-gray-500';
  };

  const getCategoryName = (categoryId) => {
    const cat = SEARCH_CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.name : '';
  };

  // Group results by category
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.category]) {
      acc[result.category] = [];
    }
    acc[result.category].push(result);
    return acc;
  }, {});

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Search Modal */}
      <div className={cn(
        'fixed top-[10%] left-1/2 -translate-x-1/2 w-[90%] max-w-2xl z-50',
        'rounded-2xl shadow-2xl overflow-hidden',
        theme === 'dark' ? 'bg-zinc-900' : 'bg-white'
      )} data-testid="global-search-modal">
        {/* Search Input */}
        <div className={cn(
          'flex items-center gap-3 p-4 border-b',
          theme === 'dark' ? 'border-white/10' : 'border-gray-200'
        )}>
          <Search size={20} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher dans l'application..."
            className={cn(
              'flex-1 bg-transparent outline-none text-lg',
              theme === 'dark' ? 'text-white placeholder:text-zinc-500' : 'text-gray-900 placeholder:text-gray-400'
            )}
            data-testid="global-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className={cn(
                'p-1 rounded-lg transition-colors',
                theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-100'
              )}
            >
              <X size={18} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-400'} />
            </button>
          )}
        </div>

        {/* Quick Categories */}
        {!query && (
          <div className="p-4">
            <p className={cn(
              'text-xs font-medium uppercase tracking-wider mb-3',
              theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'
            )}>
              Rechercher dans
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {SEARCH_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedCategory(cat);
                      inputRef.current?.focus();
                    }}
                    className={cn(
                      'flex items-center gap-2 p-3 rounded-xl transition-all',
                      theme === 'dark' 
                        ? 'bg-white/5 hover:bg-white/10 text-white' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    )}
                  >
                    <Icon size={18} className={cat.color} />
                    <span className="text-sm font-medium">{cat.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="p-4 text-center">
            <div className="inline-block w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Results */}
        {!loading && query.length >= 2 && (
          <div className="max-h-[60vh] overflow-y-auto">
            {Object.keys(groupedResults).length === 0 ? (
              <div className={cn(
                'p-8 text-center',
                theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'
              )}>
                <Search size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm">Aucun résultat pour "{query}"</p>
              </div>
            ) : (
              Object.entries(groupedResults).map(([categoryId, items]) => {
                const Icon = getCategoryIcon(categoryId);
                const category = SEARCH_CATEGORIES.find(c => c.id === categoryId);
                
                return (
                  <div key={categoryId} className="p-2">
                    <div className={cn(
                      'flex items-center justify-between px-3 py-2',
                      theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'
                    )}>
                      <div className="flex items-center gap-2">
                        <Icon size={14} className={getCategoryColor(categoryId)} />
                        <span className="text-xs font-medium uppercase tracking-wider">
                          {getCategoryName(categoryId)}
                        </span>
                      </div>
                      <button
                        onClick={() => handleCategoryClick(category)}
                        className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1"
                      >
                        Voir tout
                        <ArrowRight size={12} />
                      </button>
                    </div>
                    
                    {items.map((result, idx) => (
                      <button
                        key={`${categoryId}-${idx}`}
                        onClick={() => handleResultClick(result)}
                        className={cn(
                          'w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left',
                          theme === 'dark' 
                            ? 'hover:bg-white/5' 
                            : 'hover:bg-gray-100'
                        )}
                        data-testid={`search-result-${categoryId}-${idx}`}
                      >
                        <div className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center',
                          theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'
                        )}>
                          <Icon size={18} className={getCategoryColor(categoryId)} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            'font-medium truncate',
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          )}>
                            {highlightMatch(result.displayName, query)}
                          </p>
                          {result.subtitle && (
                            <p className={cn(
                              'text-sm truncate',
                              theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'
                            )}>
                              {result.subtitle}
                            </p>
                          )}
                        </div>
                        <ArrowRight size={16} className={theme === 'dark' ? 'text-zinc-600' : 'text-gray-300'} />
                      </button>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Keyboard Shortcut Hint */}
        <div className={cn(
          'p-3 border-t text-center text-xs',
          theme === 'dark' ? 'border-white/10 text-zinc-500' : 'border-gray-200 text-gray-400'
        )}>
          <kbd className={cn(
            'px-2 py-1 rounded mr-1',
            theme === 'dark' ? 'bg-white/10' : 'bg-gray-100'
          )}>ESC</kbd>
          pour fermer
        </div>
      </div>
    </>
  );
};

// Helper function to highlight matching text
const highlightMatch = (text, query) => {
  if (!query || !text) return text;
  
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  
  return parts.map((part, index) => 
    part.toLowerCase() === query.toLowerCase() ? (
      <span key={index} className="bg-yellow-500/30 text-yellow-600 dark:text-yellow-400 rounded px-0.5">
        {part}
      </span>
    ) : part
  );
};

// Search Trigger Button Component
export const SearchButton = ({ onClick, className }) => {
  const { theme } = useTheme();
  
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-xl transition-all',
        theme === 'dark' 
          ? 'bg-white/5 hover:bg-white/10 text-zinc-400' 
          : 'bg-gray-100 hover:bg-gray-200 text-gray-500',
        className
      )}
      data-testid="search-trigger-button"
    >
      <Search size={18} />
      <span className="text-sm hidden md:inline">Rechercher...</span>
      <kbd className={cn(
        'hidden md:inline-block px-1.5 py-0.5 text-xs rounded ml-2',
        theme === 'dark' ? 'bg-white/10' : 'bg-gray-200'
      )}>
        ⌘K
      </kbd>
    </button>
  );
};

export default GlobalSearch;
